import React, { useState } from "react";
import { Phone, Mail, Clock, MapPin, Headphones } from "lucide-react";
import Seo from "../components/Seo";
import { useToast } from "../hooks/use-toast";
import { contactInfo } from "../mock";
import { useLang } from "../context/LanguageContext";
import { createContact } from "../api";

const Contact = () => {
  const { lang, t } = useLang();
  const c = t.contact;
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) { toast({ title: c.err, variant: "destructive" }); return; }
    try {
      await createContact(form);
      toast({ title: c.okTitle, description: c.okDesc });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast({ title: c.err, variant: "destructive" });
    }
  };

  const seo = lang === "de"
    ? { title: "Kontakt & Support | MyCleaner.ch Putzfrau-Service", description: "Fragen zu Ihrer Putzfrau? Unser echter Support hilft Ihnen persönlich weiter – Telefon, E-Mail und Kontaktformular. Keine Warteschleife, kein Chatbot.", keywords: "MyCleaner Kontakt, Putzfrau Vermittlung Support, Haushaltshilfe Kontakt Schweiz" }
    : { title: "Contact & support | MyCleaner.ch cleaning service", description: "Questions about your cleaner? Our real support helps you in person – phone, email and contact form. No waiting queue, no chatbot.", keywords: "MyCleaner contact, cleaner placement support, home help contact Switzerland" };

  const items = [
    { icon: Phone, label: c.phone, value: contactInfo.phone, href: contactInfo.phoneHref },
    { icon: Mail, label: c.email, value: contactInfo.email, href: `mailto:${contactInfo.email}` },
    { icon: Clock, label: c.hoursLabel, value: c.hours },
    { icon: MapPin, label: c.areaLabel, value: c.area },
  ];

  return (
    <div>
      <Seo pageKey="contact" title={seo.title} description={seo.description} keywords={seo.keywords} />

      <section className="pt-[116px] pb-16 bg-gradient-to-b from-amber-50 to-white">
        <div className="max-w-[820px] mx-auto px-5 md:px-6 text-center">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 text-slate-600 text-sm px-4 py-1.5 rounded-full mb-4"><Headphones className="w-4 h-4 text-teal-600" /> {c.badge}</div>
          <h1 className="font-head font-extrabold text-4xl md:text-5xl leading-tight text-slate-900 mb-5">{c.title}</h1>
          <p className="text-lg text-slate-600">{c.subtitle}</p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1000px] mx-auto px-5 md:px-6 grid lg:grid-cols-2 gap-10">
          <div className="space-y-4">
            {items.map((it) => (
              <div key={it.label} className="flex items-start gap-4 bg-white rounded-2xl border border-slate-100 p-5">
                <span className="w-11 h-11 rounded-xl bg-teal-50 flex items-center justify-center shrink-0"><it.icon className="w-5 h-5 text-teal-700" /></span>
                <div>
                  <div className="text-xs text-slate-500 uppercase tracking-wide mb-1">{it.label}</div>
                  {it.href ? <a href={it.href} className="font-head font-semibold text-slate-900 hover:text-teal-700">{it.value}</a> : <div className="font-semibold text-slate-900">{it.value}</div>}
                </div>
              </div>
            ))}
          </div>

          <div className="bg-white rounded-2xl shadow-lg border border-slate-100 p-8">
            <h2 className="font-head font-bold text-xl text-slate-900 mb-5">{c.formTitle}</h2>
            <form onSubmit={submit} className="space-y-4">
              <input value={form.name} onChange={(e)=>setForm({...form,name:e.target.value})} placeholder={c.fName} className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
              <input value={form.email} onChange={(e)=>setForm({...form,email:e.target.value})} type="email" placeholder={c.fEmail} className="w-full h-12 rounded-xl border border-slate-200 px-4 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100" />
              <textarea value={form.message} onChange={(e)=>setForm({...form,message:e.target.value})} rows={5} placeholder={c.fMsg} className="w-full rounded-xl border border-slate-200 px-4 py-3 focus:outline-none focus:border-teal-600 focus:ring-2 focus:ring-teal-100 resize-none" />
              <button type="submit" className="w-full py-4 rounded-xl bg-teal-700 text-white font-head font-bold text-lg hover:bg-teal-800 transition-colors">{c.submit}</button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
